export interface ApiError extends Error {
  response: {
    data: {
      message?: string;
      error?: string;
    };
  };
}
export interface SignInPayload {
  email: string;
  password: string;
}

export interface SignUpPayload {
  email: string;
  firstName: string;
  password: string;
}

export interface ForgotPasswordPayload {
  email: string;
}
export interface ResetPasswordPayload {
  email: string;
  password: string;
  token: string;
}

export interface ForgotPassword {
  message: string;
  status?: boolean;
}

export interface RefreshTokenPayload {
  refreshToken: string;
}

export interface RefreshToken {
  accessToken: string;
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  TRIALING = 'trialing',
  UNPAID = 'unpaid',
  PAUSED = 'paused',
}

export interface AIConversation {
  id: number;
  userId: number;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  messages?: AIMessage[];
}

export interface AIMessage {
  id: number;
  conversationId: number;
  role: 'user' | 'assistant' | 'system';
  content: string | null;
  parts?: AIMessagePart[];
  createdAt: string;
}

export interface AIMessagePart {
  type: string;
  text?: string;
  toolCallId?: string;
  toolName?: string;
  arguments?: unknown;
  args?: unknown;
  output?: unknown;
  result?: unknown;
  state?: string;
}

export interface CreateConversationPayload {
  title?: string;
}

export enum Subscription {
  BASIC = 'basic',
  PREMIUM = 'premium',
  ULTIMATE = 'ultimate',
  ANNUAL = 'annual',
  HTE_ANNUAL = 'HTEAnnual',
  SPECIAL_BASE = 'specialBase',
}

export enum PlanInterval {
  MONTH = 'month',
  YEAR = 'year',
}

export interface Plan {
  id: number;
  price: number;
  name: string;
  description?: string;
  trials: number;
  search_demand: boolean;
  searches: number;
  credits: number;
  crm?: boolean;
  interval?: PlanInterval;
  unlock_suppliers?: boolean;
  show_contacts?: boolean;
  hte?: boolean;
  addon_credit_price: number;
  search_credit_price_50: number;
  search_credit_price_150: number;
  search_credit_price_500: number;
  search_credit_price_1000: number;
  credit_price_10: number;
  credit_price_25: number;
  credit_price_75: number;
  credit_price_100: number;
  begin_date?: string;
  end_date?: string;
  paddle_price_id?: string;
  stripe_price_id?: string;
  price_id?: string;

  addon_credit_price_id: string,
  search_price_50_id?: string;
  search_price_100_id?: string;
  search_price_150_id?: string;
  search_price_500_id?: string;
  search_price_1000_id?: string;
  credit_price_10_id?: string;
  credit_price_25_id?: string;
  credit_price_75_id?: string;
  credit_price_100_id?: string;

}

export interface Price {
  amount: number;
  price_id: string;
  description?: string;
}
export enum PaymentProvider {
  STRIPE = 'STRIPE',
  PADDLE = 'PADDLE',
}

export interface User {
  email: string;
  name: string;
  createdAt: string;
  accessToken: string;
  addOnCredits: number;
  creditsRemaining: number;
  customerId?: string;
  paddle_customer_id?: string;
  paddleSubscriptionId?: string;
  payment_provider?: PaymentProvider;
  subscriptionId?: string;
  subscriptionCancelAtPeriodEnd?: boolean;
  subscriptionStatus?: SubscriptionStatus;
  subscriptionExpiresAt?: string;
  plan?: Plan;
  totalCredits: number;
  churnkeyHash: string;
  trendSearchesUsed: number;
  trendSearchesRemaining: number;
  trendSearchesAddOn: number;
  customerPortalUrl: string,
  hostedCheckoutUrl: string,
  retainEnabled: boolean;
}

export interface ListOfSellersPayload {
  category?: string[];
  country?: string[];
  dropShipsTo?: string[];
  product_type?: string[];
  searchDemand?: [number, number];
  avgMargin?: [number, number];
  priceRange?: [number, number];
  map?: boolean;
  dtc?: boolean;
  avgPrice?: [number, number];
  skip?: number;
  limit?: number;
  sortModel?: {
    field: string;
    sort: 'asc' | 'desc';
  }[];
  dateAdded?: number;
  type: FavoritesType;
  verifiedMargins?: boolean;
  unlockedSuppliersOnly?: boolean;
  search?: string;
}

export enum Votes {
  UPVOTE = 'upvotes',
  DOWNVOTE = 'downvotes',
}

export enum VotesPayload {
  UPVOTE = 'upvote',
  DOWNVOTE = 'downvote',
}
export interface Representative {
  downvotes?: number;
  email: string;
  firstName: string;
  id: number;
  isVerified: boolean;
  lastName: string;
  linkedin: string;
  phone: string;
  title: string;
  upvotes?: number;
  votedByUser?: VotesPayload;
}

export enum Status {
  NO_CONTACT = 'NoContact',
  REACHED_OUT = 'ReachedOut',
  IN_DISCUSSION = 'InDiscussion',
  FOLLOW_UP = 'FollowUp',
  SIGNED_UP = 'SignedUp',
  DECLINED = 'Declined',
}
export interface Seller {
  id: number;
  name: string;
  isUnlocked?: boolean;
  supplierType?: 'DTC' | string;
  website: string;
  verifiedMargins?: boolean;
  product_types?: string[];
  country: string;
  dropShipsTo?: string[];
  information: string;
  imgSrc?: string;
  address: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  city: string;
  representatives?: Representative[];
  suppliers?: Omit<Seller, 'suppliers'>[];
  avgMargin?: number;
  avgPrice?: number;
  map?: boolean;
  marginIncludesShipping?: boolean;
  priceRange?: string;
  searchDemand?: number;
  searchDemandKeywords?: string;
  createdAt: string;
  status?: Status;
}
export interface ListOfSellers {
  rowCount: number;
  sellers: Seller[];
}

export interface UnlockPayload {
  supplierId?: number;
  supplierIds?: number[];
}

export interface ListOfProductTypesPayload {
  categories: string[];
}

export interface ProductTypeDetail {
  averageCPC?: number;
  categories: ProductCategory[];
  createdAt: string;
  id: number;
  name: string;
  searchDemand?: number;
  topMonths?: number;
  priceRange?: number[];
  seasonality?: string;
  yearlyTrend?: string;
  averagePrice?: number;
}

export interface ProductType {
  id: string;
  name: string;
}

export interface AddOnCreditsCheckoutPayload {
  quantity: number;
}

export interface AddOnCreditsPayment {
  session: {
    id: string;
  };
}

export enum FavoritesType {
  SUPPLIER = 'supplier',
  DISTRIBUTOR = 'distributor',
}

export interface Favorite extends Seller {
  favId: number;
  id: number;
  tier: number;
  favoriteFolders: number[];
  type: FavoritesType;
  notesCount: number;
}

export interface FavoriteFolder {
  folderId: number;
  folderName: string;
  favorites: string[];
}

export interface AddToFavoritesPayload {
  supplierId?: number;
  distributorId?: number;
  supplierIds?: number[];
  distributorIds?: number[];
  folderIds?: number[];
}

export interface RemoveFromFavoritePayload {
  id?: number;
  ids?: number[];
  type: FavoritesType;
}

export interface UpdateFavoriteStatusPayload {
  id: number;
  status: string;
}

export interface UpdateFavoritePayload {
  tier?: number;
  status?: string;
  folderList?: number[];
}

export interface UpdateFavoritesPayload {
  ids: number[];
  type: FavoritesType;
  folderList?: number[];
}

export interface SuggestCorrectionPayload {
  margin: string;
  marginIncludesShipping: string;
  comment?: string;
  supplier: string;
}

export interface SubscriptionCheckoutPayload {
  plan_id: number;
  payment_provider: PaymentProvider;
  price_id?: string;
}

export enum AcademyType {
  VIDEO = 'VIDEO',
  PDF = 'PDF',
  DOCX = 'DOCX',
  CSV = 'CSV',
}

export interface AcademyProduct {
  createdAt: string;
  description: string;
  fileLink?: string;
  id: number;
  imageUrl: string;
  title: string;
  type: string;
  videoLink?: string;
}

export interface Academy {
  AcademyType: AcademyProduct;
}

export interface ProductCategory {
  id: number;
  name: string;
  createdAt: string;
}

export interface Country {
  name: string;
  id: number;
}

export enum AdvancedSearchType {
  SELLER = 'seller',
  PRODUCT = 'product',
}

export enum AdvancedSearchGroup {
  SUPPLIER = 'supplier',
  DISTRIBUTOR = 'distributor',
  PRODUCT_TYPE = 'productType',
  CATEGORY = 'category',
}

export interface AdvancedSearch {
  group: AdvancedSearchGroup;
  type: AdvancedSearchType;
  rowCount: number;
  data?: {
    id: number | string;
    isUnlocked?: boolean;
    name: string;
    website?: string;
    imgSrc?: string;
  }[];
}

export interface ResentSearchDemand {
  name: string;
  date: string;
  date_15: string;
  volume: number;
  global_volume: number;
  cpc: number;
  competition: number;
  intent: number;
  keyword_difficulty: number;
  yearlyTrend: string;
  seasonality: string;
  updatedAt: string;
  country: string;
}

export interface SearchDemand {
  keyword: string;
  volume: string;
  globalVolume: number;
  cpc: string;
  competition: string;
  numberOfSearches: string;
  intent: string;
  keywordDifficulty: string;
  yearlyTrend: string;
  seasonality: string;
  volumeLastMonthOfPrevYear: string;
  countryVolumes: {
    country: string;
    volume: number;
  }[];
  trendSearchesUsed: number;
  trendSearchesRemaining: number;
}

export interface FavoriteNote {
  id: number;
  body: string;
  createdAt: string;
  editedAt: string | null;
}

export interface CreateFavoriteNotePayload {
  body: string;
}
